import { useSyncExternalStore } from 'react';
import type { AssetPackSetting } from '../game/prefs.js';
import { discordEmojiUrl } from './keys.js';

/**
 * Local copy of the bot's artwork produced by `npm run assets:mirror`
 * (git-ignored, served from public/assets/mirror/). When present it is the
 * preferred source for the "original artwork" pack; otherwise the pack
 * hot-links Discord and every image falls back to generated art on error.
 */
interface Manifest {
  version: 1;
  entries: Record<string, string>;
}

export type MirrorStatus = 'idle' | 'loading' | 'ready' | 'absent';

let status: MirrorStatus = 'idle';
let entries: Record<string, string> = {};
const listeners = new Set<() => void>();

function emit(next: MirrorStatus): void {
  status = next;
  for (const l of listeners) l();
}

export function loadMirror(fetchImpl: typeof fetch | undefined = globalThis.fetch): void {
  if (status !== 'idle' || !fetchImpl) return;
  emit('loading');
  fetchImpl('assets/mirror/manifest.json', { cache: 'no-cache' })
    .then((r) => (r.ok ? r.json() : null))
    .then((json: unknown) => {
      const m = json as Manifest | null;
      if (m && m.version === 1 && m.entries && Object.keys(m.entries).length > 0) {
        entries = m.entries;
        emit('ready');
      } else emit('absent');
    })
    .catch(() => emit('absent'));
}

/** Test hook. */
export function setMirrorForTests(next: Record<string, string> | null): void {
  entries = next ?? {};
  emit(next ? 'ready' : 'absent');
}

export function mirrorCount(): number {
  return Object.keys(entries).length;
}

export function useMirrorStatus(): MirrorStatus {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => status,
  );
}

/** Artwork (portrait, chapter art, speaker avatar) URL for the active pack, or null for generated art. */
export function resolveArtwork(url: string | undefined, pack: AssetPackSetting): string | null {
  if (!url || pack === 'glyph') return null;
  return entries[url] ?? url;
}

/** Emoji image URL for the active pack, or null for generated art. */
export function resolveEmoji(id: string, animated: boolean, pack: AssetPackSetting): string | null {
  if (!id || pack === 'glyph') return null;
  return entries[`emoji:${id}`] ?? discordEmojiUrl(id, animated);
}
